import { useContext, createContext } from 'react';
import {
  applySnapshot,
  cast,
  detach,
  getSnapshot,
  Instance,
  IStateTreeNode,
  IType,
  onSnapshot,
  types,
} from 'mobx-state-tree';
import makeInspectable from 'mobx-devtools-mst';
import { Moment } from 'moment';
import { EducationQualificationType, EmploymentExperienceType } from '@modules/inquiry';
import { InquiryType } from '@app-types/graphql-types';
import { Student } from '@models/Student';
import { ResultSheet } from '@models/ResultSheet';
import { User } from './User';
import { Inquiry } from './Inquiry';

import { EmploymentExperiences } from './EmploymentExperiences';
import { Timetable } from './Timetable';

const initialState = {
  user: {},
  student: {},
  inquiries: [],
  timetable: {
    dragHoverTime: null,
  },
  resultSheet: {
    studentResultSheets: [],
  },
};

const Root = types
  .model('RootModel', {
    user: User,
    student: Student,
    inquiries: types.optional(types.array(Inquiry), []),
    timetable: Timetable,
    resultSheet: ResultSheet,
    isLoading: false,
    tourOpen: false,
  })
  .actions((self) => ({
    reset() {
      applySnapshot(self, {
        ...initialState,
      });
    },
    setTourOpen(tourOpen: boolean) {
      self.tourOpen = tourOpen;
    },
    addInquiry(inquiryData: {
      id: string;
      inquirerName: string;
      firstName: string;
      middleName: string;
      lastName: string;
      city: string;
      date: Moment;
      inquirerContactNo: string;
      inquirerEmail: string;
      dateOfBirth: Moment;
      gender: string;
      educationQualifications: EducationQualificationType[];
      employmentExperiences: EmploymentExperienceType[];
      courseID: string;
      preferredYear: number;
      intakeID: string;
      assignedUserID: string;
      inquiryType: InquiryType;
      referredVia?: string[];
    }) {
      const existingInquiryIndex = self.inquiries.find((inquiry) => {
        return inquiry.id === inquiryData.id;
      });

      let employmentExperiences: Instance<typeof EmploymentExperiences>[] = [];

      if (inquiryData.employmentExperiences) {
        employmentExperiences = inquiryData.employmentExperiences.map((employmentExperience) => ({
          startDate: employmentExperience.startDate ? employmentExperience.startDate.toDate() : undefined,
          endDate: employmentExperience.endDate ? employmentExperience.endDate.toDate() : undefined,
          position: employmentExperience.position,
          company: employmentExperience.company,
          duties: employmentExperience.duties,
          references: employmentExperience.references,
        }));
      }

      if (existingInquiryIndex) {
        const newInquiriesData: Instance<typeof Inquiry> = cast({
          ...existingInquiryIndex,
          ...inquiryData,
          employmentExperiences,
          date: inquiryData.date ? inquiryData.date.toDate() : undefined,
          dateOfBirth: inquiryData.dateOfBirth ? inquiryData.dateOfBirth.toDate() : undefined,
          updatedAt: new Date(),
        });

        if (!inquiryData.referredVia && existingInquiryIndex && existingInquiryIndex.referredVia) {
          newInquiriesData.referredVia = cast(
            getSnapshot(
              existingInquiryIndex.referredVia as IStateTreeNode<IType<string, string[] | undefined, string>>,
            ),
          );
        }

        self.inquiries = cast(self.inquiries.filter((inquiry) => inquiry.id !== inquiryData.id));
        self.inquiries.push(newInquiriesData);
      } else {
        self.inquiries.push({
          ...inquiryData,
          employmentExperiences,
          date: inquiryData.date ? inquiryData.date.toDate() : undefined,
          dateOfBirth: inquiryData.dateOfBirth ? inquiryData.dateOfBirth.toDate() : undefined,
          savedAt: new Date(),
          updatedAt: new Date(),
        });
      }
    },
    removeInquiry(inquiryDraftId: string) {
      // Get index of the draft inquiry need to be deleted
      const draftInquiryIndex = self.inquiries.findIndex((inquiry) => inquiry.id === inquiryDraftId);

      // Delete inquiry form store if index exist
      if (draftInquiryIndex >= 0) {
        detach(self.inquiries[draftInquiryIndex]);
      }
    },
    setIsLoading(isLoading: boolean) {
      self.isLoading = isLoading;
    },
  }))
  .views((self) => ({
    getInquiry(inquiryDraftID: string): Instance<typeof Inquiry> | undefined {
      return self.inquiries.find((inquiry) => {
        return inquiry.id === inquiryDraftID;
      });
    },
  }));

const localSnapshot = process.browser ? localStorage.getItem('hsmsRootStore') : null;

export type RootStoreTypeType = Instance<typeof Root>;

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export const rootStore: RootStoreTypeType = makeInspectable(
  Root.create(localSnapshot ? JSON.parse(localSnapshot) : initialState),
);

onSnapshot(rootStore, (snapshot) => {
  if (process.browser) {
    localStorage.setItem('hsmsRootStore', JSON.stringify(snapshot));
  }
});

export type RootInstance = Instance<typeof Root>;
const RootStoreContext = createContext<null | RootInstance>(null);

export const { Provider } = RootStoreContext;
export function useMst(): RootInstance {
  const store = useContext(RootStoreContext);
  if (store === null) {
    throw new Error('Store cannot be null, please add a context provider');
  }
  return store as RootInstance;
}
